/* eslint-disable no-param-reassign */

import _ from 'underscore';
import { I18nModel } from '@bingads-webui-universal/i18n-model';

import { LegacyI18nLoader } from './base-loader';
import allStringsMap from './all-strings';
import allStringsOverride from './all-strings-override';

// Every source text the legacy bundle can legitimately be asked for directly, i.e. the values
// of allStringsMap plus every override map. Built lazily because it is only consulted on the
// rare unmapped-key path, and because the maps hold ~23K entries.
let sourceTexts = null;

function isKnownSourceText(key) {
  if (!sourceTexts) {
    sourceTexts = new Set(_.values(allStringsMap));
    _.each(allStringsOverride, overrideMap => _.each(overrideMap, text => sourceTexts.add(text)));
  }

  return sourceTexts.has(key);
}

function getMappedKey(i18nName, key) {
  const overrideMap = allStringsOverride[i18nName];
  if (overrideMap && _.has(overrideMap, key)) {
    return overrideMap[key];
  }

  return _.has(allStringsMap, key) ? allStringsMap[key] : undefined;
}

// The shared all-strings resource can be sparse (build-time English identity/null pruning, see
// omitLegacyEnglishIdentityTranslations). A sparse resource carries
// missingTranslationFallback: 'key', so getString() answers ANY key with the key itself and can
// no longer be used to probe whether a key is known -- read the dictionary directly instead.
// A module-level helper rather than a method so it can never collide with a future I18nModel API.
function hasTranslation(model, key) {
  const promise = model.p$data;
  if (!promise || !_.isFunction(promise.isFulfilled) || !promise.isFulfilled()) {
    return false;
  }
  const { data } = promise.value().resource;

  return _.isObject(data) && _.has(data, key);
}

export class AllStringsI18nModelClone extends I18nModel {
  constructor({
    i18nName,
    i18nToClone,
    overrideLocale,
    overrideTimeZone,
    overrideCurrency,
  }) {
    super(_.defaults({
      loadTranslationData: () => ({}),
      failFast: false,
      locale: overrideLocale || i18nToClone.locale,
      timeZone: overrideTimeZone || i18nToClone.timeZone,
      currency: overrideCurrency || i18nToClone.currency,
    }, i18nToClone));
    this.p$data = i18nToClone.p$data;
    this.i18nName = i18nName;
  }

  getString(key, ...args) {
    return this.getLegacyString(key, ...args);
  }

  getLegacyString(key, ...args) {
    const mappedKey = getMappedKey(this.i18nName, key);
    // There are some missing keys by mistake
    if (!mappedKey) {
      // Some places are using mergei18n hacks, such as responsive ad editor, and some pass the
      // source text straight through. Legacy callers also probe absent keys to select a default,
      // so do not infer source-text intent from the spelling of an unknown key.
      if (hasTranslation(this, key) || isKnownSourceText(key)) {
        const tryGetString = super.getString(key, ...args);

        if (tryGetString) {
          return tryGetString;
        }
      }

      // Mistakes in feature code sometimes mean keys are missing,
      // but current tests/product rely on this behavior
      // (ModuleI18nShim.getStringOrDefault falls back to its default value on undefined).

      return undefined;
    }

    return super.getString(mappedKey, ...args);
  }

  getSourceString(key, ...args) {
    return super.getString(getMappedKey(this.i18nName, key) || key, ...args);
  }

  fetchString(key, ...args) {
    return this.p$data.then(() => this.getString(key, ...args));
  }

  forSourceText() {
    if (!this.sourceTextI18n) {
      // eslint-disable-next-line no-use-before-define
      this.sourceTextI18n = new SourceTextI18nModel({
        i18nName: this.i18nName,
        i18nToClone: this,
      });
    }

    return this.sourceTextI18n;
  }
}

// Only modern rendering boundaries opt in. The original model and legacy shims keep
// their missing-key contract; both views share translations, not lookup policy.
class SourceTextI18nModel extends AllStringsI18nModelClone {
  getString(key, ...args) {
    return this.getSourceString(key, ...args);
  }

  forSourceText() {
    return this;
  }
}

export class AllStringsLoader extends LegacyI18nLoader {
  loadI18nData({
    loaderName,
    i18nName,
    overrideLocale,
  }) {
    if (this.isMultipleCultures(i18nName)) {
      // Special exception that stil uses legacy loader:
      return super.loadI18nData({
        loaderName,
        i18nName,
        overrideLocale,
      });
    }
    // Not needed anymore, strings come from allStrings.
    return {};
  }

  isMultipleCultures(i18nName) {
    return i18nName === 'campaignui/feature/adextensions/multiple-cultures';
  }

  cloneI18n({
    i18nName,
    i18nToClone,
    legacyI18nData,
    overrideLocale,
    overrideTimeZone,
    overrideCurrency,
  }) {
    if (this.isMultipleCultures(i18nName)) {
      return super.cloneI18n({
        i18nName,
        i18nToClone,
        overrideLocale,
        overrideTimeZone,
        overrideCurrency,
        legacyI18nData,
      });
    }

    return new AllStringsI18nModelClone({
      i18nName,
      i18nToClone,
      overrideLocale,
      overrideTimeZone,
      overrideCurrency,
    });
  }

  loadNormalI18n({
    loaderName,
    i18nName,
    overrideLocale,
    overrideTimeZone,
    overrideCurrency,
  }) {
    if (this.isMultipleCultures(i18nName)) {
      super.loadNormalI18n({
        loaderName,
        i18nName,
        overrideLocale,
        overrideTimeZone,
        overrideCurrency,
        i18nToClone: this.main,
      });
    }

    return super.loadNormalI18n({
      loaderName,
      i18nName,
      overrideLocale,
      overrideTimeZone,
      overrideCurrency,
      // Must initialize in registry
      i18nToClone: this.registry.allStrings,
    });
  }
}
